import { useState } from 'react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarIcon } from 'lucide-react';
import { format, subDays, subMonths, startOfYear } from 'date-fns';

export interface DateRange {
  from?: Date;
  to?: Date;
}

interface DateRangeFilterProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
}

const presetRanges = [
  { label: 'Last 30 days', value: 'last-30-days' },
  { label: 'Last 3 months', value: 'last-3-months' },
  { label: 'Last 6 months', value: 'last-6-months' },
  { label: 'This year', value: 'this-year' },
  { label: 'Custom', value: 'custom' },
];

export function DateRangeFilter({ dateRange, onDateRangeChange }: DateRangeFilterProps) {
  const [selectedPreset, setSelectedPreset] = useState('last-6-months');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handlePresetChange = (preset: string) => {
    setSelectedPreset(preset);
    
    const today = new Date();
    let newRange: DateRange;

    switch (preset) {
      case 'last-30-days':
        newRange = {
          from: subDays(today, 30),
          to: today,
        };
        break;
      case 'last-3-months':
        newRange = {
          from: subMonths(today, 3),
          to: today,
        };
        break;
      case 'last-6-months':
        newRange = {
          from: subMonths(today, 6),
          to: today,
        };
        break;
      case 'this-year':
        newRange = {
          from: startOfYear(today),
          to: today,
        };
        break;
      case 'custom':
        // Keep current range for custom selection
        return;
      default:
        return;
    }

    onDateRangeChange(newRange);
  };

  const formatDateRange = () => {
    if (!dateRange.from || !dateRange.to) {
      return 'Select date range';
    }
    
    return `${format(dateRange.from, 'MMM dd, yyyy')} - ${format(dateRange.to, 'MMM dd, yyyy')}`;
  };

  return (
    <div className="flex flex-col gap-3" style={{ fontSize: '11.2px' }}>
      <div className="flex gap-2">
        <Select value={selectedPreset} onValueChange={handlePresetChange}>
          <SelectTrigger className="w-40" style={{ fontSize: '11.2px' }}>
            <SelectValue placeholder="Select range" />
          </SelectTrigger>
          <SelectContent>
            {presetRanges.map((range) => (
              <SelectItem key={range.value} value={range.value} style={{ fontSize: '11.2px' }}>
                {range.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedPreset === 'custom' && (
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-64 justify-start text-left font-normal"
                style={{ fontSize: '11.2px' }}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formatDateRange()}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange.from}
                selected={{ from: dateRange.from, to: dateRange.to }}
                onSelect={(range) => {
                  if (range) {
                    onDateRangeChange(range);
                  }
                }}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}